import { getTwitterUsers } from '../services/twitterService';
import { subnetNames } from './subnetNames';
import { subnetDescriptions } from './subnetDescriptions';
import type { TwitterUser } from '../types/twitter';
import type { SubnetMindshare } from '../types/mindshare';
import axios from 'axios';
import { rateLimiter } from '../services/rateLimiter';
import { getMindshareHistory } from '../services/mindshareService';
import { cacheService } from '../services/cacheService';
import { clearAllCaches, getCorrectSubnetName } from './cacheUtils';

interface SubnetPool {
  netuid: number;
  name: string;
  symbol: string;
  rank: number;
  market_cap: string;
  liquidity: string;
  total_tao: string;
  total_alpha: string;
  alpha_in_pool: string;
  alpha_staked: string;
  price: string;
  price_change_1_hour: string;
  price_change_1_day: string;
  tao_volume_24_hr: string;
  tao_buy_volume_24_hr: string;
  tao_sell_volume_24_hr: string;
  buys_24_hr: number;
  sells_24_hr: number;
}

// Weights for different factors in mindshare calculation
const WEIGHTS = {
  TWITTER_ENGAGEMENT: 0.40,  // 40% weight for Twitter engagement
  MARKET_CAP: 0.10,          // 10% weight for market cap
  RANK: 0.10,                // 10% weight for subnet rank (inverse)
  VOLUME: 0.35               // 35% weight for trading volume
};

// Cache for percentile thresholds to ensure consistency between calculations
let cachedPercentileThresholds: {
  engagement: { p75: number, max: number },
  marketCap: { p75: number, p90: number, max: number },
  volume: { p75: number, p90: number, max: number }
} | null = null;

// Fetch all subnet data from API
const fetchAllSubnets = async (): Promise<SubnetPool[]> => {
  try {
    // Always clear cache to ensure fresh data
    clearAllCaches();
    
    await rateLimiter.checkRateLimit();
    
    const response = await axios.get(
      'https://api.taostats.io/api/dtao/pool/latest/v1?page=1&limit=100&order=netuid_asc',
      {
        headers: {
          'accept': 'application/json',
          'Authorization': 'tao-1b237a3c-dcb4-468c-ad70-6b47e40e2552:6473c9a3'
        }
      }
    );

    if (!response.data || !Array.isArray(response.data.data)) {
      throw new Error('Invalid response format');
    }

    const subnets = response.data.data.filter((subnet: SubnetPool) => 
      subnet.netuid !== 0 && 
      subnet.market_cap && 
      !isNaN(Number(subnet.market_cap))
    );
    
    return subnets;
  } catch (error) {
    console.error('Failed to fetch subnet data:', error);
    return [];
  }
};

// Match Twitter users to subnets
const matchTwitterUsersToSubnets = (twitterUsers: TwitterUser[]): Record<number, TwitterUser | null> => {
  const subnetTwitterUsers: Record<number, TwitterUser | null> = {};
  
  // For each subnet with X/Twitter info
  Object.entries(subnetDescriptions).forEach(([netuidStr, info]) => {
    const netuid = parseInt(netuidStr);
    
    // Skip subnets with no Twitter info or empty Twitter URL
    if (!info.x || info.x.trim() === '') {
      console.log(`No Twitter URL for subnet ${netuid} (${getCorrectSubnetName(netuid, subnetNames)}), skipping`);
      subnetTwitterUsers[netuid] = null;
      return;
    }
    
    // Extract username from X/Twitter URL
    const xUrlMatch = info.x.match(/twitter\.com\/(\w+)|x\.com\/(\w+)/i);
    if (!xUrlMatch) {
      console.log(`Invalid Twitter URL format for subnet ${netuid} (${getCorrectSubnetName(netuid, subnetNames)}): ${info.x}`);
      subnetTwitterUsers[netuid] = null;
      return;
    }
    
    const xUsername = (xUrlMatch[1] || xUrlMatch[2] || '').toLowerCase();
    
    // Find matching Twitter user
    const matchingUser = twitterUsers.find(user => 
      user.username.toLowerCase() === xUsername
    );
    
    // Log for debugging
    console.log(`Matching Twitter for subnet ${netuid} (${getCorrectSubnetName(netuid, subnetNames)}): ${xUsername} -> ${matchingUser ? 'Found' : 'Not found'}`);
    
    subnetTwitterUsers[netuid] = matchingUser || null;
  });
  
  return subnetTwitterUsers;
};

// Calculate Twitter engagement score with logarithmic scaling
const calculateTwitterEngagement = (
  twitterUser: TwitterUser | null,
  engagementValues: number[],
  maxEngagement: number,
  percentileThresholds: { p75: number, max: number }
): number => {
  if (!twitterUser) {
    console.log('No Twitter user found, returning 0 engagement score');
    return 0;
  }
  
  // Check if the engagement data is available and valid
  if (!twitterUser.engagement || 
      typeof twitterUser.engagement.total_likes !== 'number' ||
      typeof twitterUser.engagement.total_quotes !== 'number' ||
      typeof twitterUser.engagement.total_replies !== 'number' ||
      typeof twitterUser.engagement.total_retweets !== 'number') {
    console.log('Invalid engagement data structure, returning 0 engagement score');
    return 0;
  }
  
  const engagement = 
    twitterUser.engagement.total_likes + 
    twitterUser.engagement.total_quotes * 3 + 
    twitterUser.engagement.total_replies * 2 + 
    twitterUser.engagement.total_retweets * 3;
  
  if (engagement <= 0) {
    console.log('Zero engagement value, returning 0 engagement score');
    return 0;
  }
  
  // Use cached percentile thresholds for consistency
  const percentile75Value = percentileThresholds.p75;
  const maxValue = percentileThresholds.max;
  
  // Use logarithmic scaling for high engagement values
  // This compresses the upper range while preserving differentiation in the lower range
  const normalizedScore = Math.min(100, 
    (engagement <= percentile75Value) 
      ? (engagement / percentile75Value) * 75  // Linear scaling up to 75th percentile
      : 75 + (25 * (Math.log(engagement / percentile75Value) / Math.log(maxValue / percentile75Value)))  // Logarithmic scaling above 75th percentile
  );
  
  console.log(`Calculated Twitter engagement score: ${normalizedScore.toFixed(2)} (raw engagement: ${engagement})`);
  return normalizedScore;
};

// Calculate market cap score with moderate handling of outliers
const calculateMarketCapScore = (
  marketCap: string,
  marketCapValues: number[],
  maxMarketCap: number,
  percentileThresholds: { p75: number, p90: number, max: number }
): number => {
  const marketCapValue = Number(marketCap);
  if (isNaN(marketCapValue) || marketCapValue <= 0) return 0;
  
  // Use cached percentile thresholds for consistency
  const percentile75Value = percentileThresholds.p75;
  const percentile90Value = percentileThresholds.p90;
  const maxValue = percentileThresholds.max;
  
  // Determine the scaling approach based on the market cap value
  let normalizedScore: number;
  
  if (marketCapValue <= percentile75Value) {
    // Below 75th percentile: linear scaling from 0-75
    normalizedScore = (marketCapValue / percentile75Value) * 75;
  } else if (marketCapValue <= percentile90Value) {
    // Between 75th and 90th percentile: linear scaling from 75-90
    normalizedScore = 75 + ((marketCapValue - percentile75Value) / (percentile90Value - percentile75Value)) * 15;
  } else {
    // Above 90th percentile: mild logarithmic scaling from 90-100
    // This compresses the upper range but not as aggressively as for volume
    normalizedScore = 90 + (10 * (Math.log(marketCapValue / percentile90Value) / Math.log(maxValue / percentile90Value)));
  }
  
  // Ensure the score is within 0-100 range
  return Math.min(100, Math.max(0, normalizedScore));
};

// Calculate rank score (inverse - lower rank is better)
const calculateRankScore = (rank: number, totalSubnets: number): number => {
  if (rank <= 0) return 0;
  
  // Inverse and normalize to 0-100 scale
  // Use a more gradual curve to prevent extreme differences
  const normalizedRank = Math.max(0, 100 - ((rank / totalSubnets) * 80));
  
  return normalizedRank;
};

// Calculate volume score with stronger logarithmic scaling to handle anomalies
const calculateVolumeScore = (
  volume: string,
  volumeValues: number[],
  maxVolume: number,
  percentileThresholds: { p75: number, p90: number, max: number }
): number => {
  const volumeValue = Number(volume);
  if (isNaN(volumeValue) || volumeValue <= 0) return 0;
  
  // Use cached percentile thresholds for consistency
  const percentile75Value = percentileThresholds.p75;
  const percentile90Value = percentileThresholds.p90;
  const maxValue = percentileThresholds.max;
  
  // Determine the scaling approach based on the volume value
  let normalizedScore: number;
  
  if (volumeValue <= percentile75Value) {
    // Below 75th percentile: linear scaling from 0-75
    normalizedScore = (volumeValue / percentile75Value) * 75;
  } else if (volumeValue <= percentile90Value) {
    // Between 75th and 90th percentile: linear scaling from 75-90
    normalizedScore = 75 + ((volumeValue - percentile75Value) / (percentile90Value - percentile75Value)) * 15;
  } else {
    // Above 90th percentile: strong logarithmic scaling from 90-100
    // This heavily compresses the upper range to handle extreme outliers
    normalizedScore = 90 + (10 * (Math.log(volumeValue / percentile90Value) / Math.log(maxValue / percentile90Value)));
  }
  
  // Ensure the score is within 0-100 range
  return Math.min(100, Math.max(0, normalizedScore));
};

// Calculate total mindshare score
const calculateTotalScore = (
  twitterEngagement: number,
  marketCapScore: number,
  rankScore: number,
  volumeScore: number
): number => {
  return (
    twitterEngagement * WEIGHTS.TWITTER_ENGAGEMENT +
    marketCapScore * WEIGHTS.MARKET_CAP +
    rankScore * WEIGHTS.RANK +
    volumeScore * WEIGHTS.VOLUME
  );
};

// Calculate mindshare percentages
const calculateMindsharePercentages = (
  mindshareScores: SubnetMindshare[]
): SubnetMindshare[] => {
  const totalScore = mindshareScores.reduce((sum, item) => sum + item.totalScore, 0);
  
  if (totalScore <= 0) {
    return mindshareScores.map(item => ({
      ...item,
      mindsharePercentage: 0
    }));
  }
  
  return mindshareScores.map(item => ({
    ...item,
    mindsharePercentage: (item.totalScore / totalScore) * 100
  }));
};

// Apply a minimum baseline score to ensure no subnet gets completely zeroed out
const applyMinimumBaseline = (scores: SubnetMindshare[]): SubnetMindshare[] => {
  // Find the maximum score for each category
  const maxTwitter = Math.max(...scores.map(s => s.twitterEngagement));
  const maxMarketCap = Math.max(...scores.map(s => s.marketCapScore));
  const maxRank = Math.max(...scores.map(s => s.rankScore));
  const maxVolume = Math.max(...scores.map(s => s.volumeScore));
  
  // Define minimum thresholds (5% of max score)
  const minTwitter = maxTwitter * 0.05;
  const minMarketCap = maxMarketCap * 0.05;
  const minRank = maxRank * 0.05;
  const minVolume = maxVolume * 0.05;
  
  // Apply minimum thresholds to ensure no subnet gets completely zeroed out
  return scores.map(score => {
    // Only apply minimum if the subnet has some data (not completely empty)
    const hasData = score.twitterEngagement > 0 || 
                    score.marketCapScore > 0 || 
                    score.rankScore > 0 || 
                    score.volumeScore > 0;
    
    if (hasData) {
      const updatedScore = {
        ...score,
        twitterEngagement: score.twitterEngagement > 0 ? Math.max(score.twitterEngagement, minTwitter) : 0,
        marketCapScore: score.marketCapScore > 0 ? Math.max(score.marketCapScore, minMarketCap) : 0,
        rankScore: score.rankScore > 0 ? Math.max(score.rankScore, minRank) : 0,
        volumeScore: score.volumeScore > 0 ? Math.max(score.volumeScore, minVolume) : 0
      };
      
      // Recalculate total score
      updatedScore.totalScore = calculateTotalScore(
        updatedScore.twitterEngagement,
        updatedScore.marketCapScore,
        updatedScore.rankScore,
        updatedScore.volumeScore
      );
      
      return updatedScore;
    }
    
    return score;
  });
};

// Calculate percentile thresholds for consistent scoring
const calculatePercentileThresholds = (
  engagementValues: number[],
  marketCapValues: number[],
  volumeValues: number[]
) => {
  // Sort values for percentile calculations
  const sortedEngagements = [...engagementValues].sort((a, b) => a - b);
  const sortedMarketCaps = [...marketCapValues].sort((a, b) => a - b);
  const sortedVolumes = [...volumeValues].sort((a, b) => a - b);
  
  // Calculate percentile indices
  const engagementP75Index = Math.floor(sortedEngagements.length * 0.75);
  const marketCapP75Index = Math.floor(sortedMarketCaps.length * 0.75);
  const marketCapP90Index = Math.floor(sortedMarketCaps.length * 0.9);
  const volumeP75Index = Math.floor(sortedVolumes.length * 0.75);
  const volumeP90Index = Math.floor(sortedVolumes.length * 0.9);
  
  // Get percentile values
  const engagementP75 = sortedEngagements[engagementP75Index] || Math.max(...engagementValues, 1);
  const marketCapP75 = sortedMarketCaps[marketCapP75Index] || Math.max(...marketCapValues, 1);
  const marketCapP90 = sortedMarketCaps[marketCapP90Index] || Math.max(...marketCapValues, 1);
  const volumeP75 = sortedVolumes[volumeP75Index] || Math.max(...volumeValues, 1);
  const volumeP90 = sortedVolumes[volumeP90Index] || Math.max(...volumeValues, 1);
  
  // Get maximum values
  const maxEngagement = Math.max(...engagementValues, 1);
  const maxMarketCap = Math.max(...marketCapValues, 1);
  const maxVolume = Math.max(...volumeValues, 1);
  
  return {
    engagement: { p75: engagementP75, max: maxEngagement },
    marketCap: { p75: marketCapP75, p90: marketCapP90, max: maxMarketCap },
    volume: { p75: volumeP75, p90: volumeP90, max: maxVolume }
  };
};

// Calculate percentage changes from history data
export const calculatePercentChanges = async (subnets: SubnetMindshare[]): Promise<Record<number, number>> => {
  try {
    // Clear cache to ensure fresh data
    clearAllCaches();
    
    // Get last 24 hours of data for each subnet
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    
    const changesData = await Promise.all(
      subnets.map(async subnet => {
        try {
          const history = await getMindshareHistory(subnet.netuid);
          if (!history || history.history.length < 2) return null;
          
          // Filter for last 24h and sort by timestamp
          const filteredHistory = history.history
            .filter(entry => new Date(entry.timestamp) >= oneDayAgo)
            .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
          
          if (filteredHistory.length < 2) return null;
          
          const oldestEntry = filteredHistory[0];
          const newestEntry = filteredHistory[filteredHistory.length - 1];
          
          const oldValue = oldestEntry.mindsharePercentage;
          const newValue = newestEntry.mindsharePercentage;
          
          let percentChange = 0;
          if (oldValue > 0) {
            percentChange = ((newValue - oldValue) / oldValue) * 100;
          }
          
          return {
            netuid: subnet.netuid,
            percentChange
          };
        } catch (error) {
          console.error(`Error processing subnet ${subnet.netuid}:`, error);
          return null;
        }
      })
    );
    
    // Convert to record for easy lookup
    const changesRecord: Record<number, number> = {};
    changesData
      .filter((item): item is NonNullable<typeof changesData[0]> => item !== null)
      .forEach(item => {
        changesRecord[item.netuid] = item.percentChange;
      });
    
    return changesRecord;
  } catch (error) {
    console.error('Error calculating percentage changes:', error);
    return {};
  }
};

// Main function to calculate subnet mindshare
export const calculateSubnetMindshare = async (): Promise<SubnetMindshare[]> => {
  try {
    // Always clear cache to ensure fresh data
    clearAllCaches();
    
    // Reset cached percentile thresholds to ensure fresh calculation
    cachedPercentileThresholds = null;
    
    // Fetch all required data
    const [subnets, twitterUsers] = await Promise.all([
      fetchAllSubnets(),
      getTwitterUsers(100)
    ]);
    
    if (subnets.length === 0) {
      throw new Error('No subnet data available');
    }
    
    // Match Twitter users to subnets
    const subnetTwitterUsers = matchTwitterUsersToSubnets(twitterUsers);
    
    // Log Twitter matches for debugging
    console.log('Twitter user matches:', Object.entries(subnetTwitterUsers)
      .filter(([_, user]) => user !== null)
      .map(([netuid, user]) => `${netuid} (${getCorrectSubnetName(parseInt(netuid), subnetNames)}): @${user?.username}`)
    );
    
    // Calculate engagement for all subnets with Twitter accounts
    const engagementValues = Object.values(subnetTwitterUsers)
      .filter((user): user is TwitterUser => user !== null)
      .map(user => {
        // Ensure engagement data exists and is valid
        if (!user.engagement || 
            typeof user.engagement.total_likes !== 'number' ||
            typeof user.engagement.total_quotes !== 'number' ||
            typeof user.engagement.total_replies !== 'number' ||
            typeof user.engagement.total_retweets !== 'number') {
          return 0;
        }
        
        return user.engagement.total_likes + 
               user.engagement.total_quotes * 3 + 
               user.engagement.total_replies * 2 + 
               user.engagement.total_retweets * 3;
      })
      .filter(val => val > 0);
    
    // If no valid engagement values, use a default array to prevent calculation errors
    if (engagementValues.length === 0) {
      console.log('No valid engagement values found, using default values');
      engagementValues.push(1, 10, 100); // Add some default values to prevent division by zero
    }
    
    // Extract all market cap and volume values for percentile calculations
    const marketCapValues = subnets.map(subnet => Number(subnet.market_cap) || 0).filter(val => val > 0);
    const volumeValues = subnets.map(subnet => Number(subnet.tao_volume_24_hr) || 0).filter(val => val > 0);
    
    // Calculate or use cached percentile thresholds
    if (!cachedPercentileThresholds) {
      cachedPercentileThresholds = calculatePercentileThresholds(
        engagementValues,
        marketCapValues,
        volumeValues
      );
      console.log('Calculated new percentile thresholds:', cachedPercentileThresholds);
    } else {
      console.log('Using cached percentile thresholds for consistency');
    }
    
    const totalSubnets = Object.keys(subnetNames).length;
    
    // Calculate mindshare scores for each subnet
    let mindshareScores: SubnetMindshare[] = subnets.map(subnet => {
      const twitterUser = subnetTwitterUsers[subnet.netuid] || null;
      
      // Log Twitter engagement calculation for debugging
      console.log(`Calculating Twitter engagement for subnet ${subnet.netuid} (${getCorrectSubnetName(subnet.netuid, subnetNames)})`);
      console.log(`Twitter user: ${twitterUser ? '@' + twitterUser.username : 'Not found'}`);
      
      const twitterEngagement = calculateTwitterEngagement(
        twitterUser, 
        engagementValues, 
        cachedPercentileThresholds!.engagement.max,
        cachedPercentileThresholds!.engagement
      );
      
      const marketCapScore = calculateMarketCapScore(
        subnet.market_cap, 
        marketCapValues, 
        cachedPercentileThresholds!.marketCap.max,
        cachedPercentileThresholds!.marketCap
      );
      
      const rankScore = calculateRankScore(subnet.rank, totalSubnets);
      
      const volumeScore = calculateVolumeScore(
        subnet.tao_volume_24_hr, 
        volumeValues, 
        cachedPercentileThresholds!.volume.max,
        cachedPercentileThresholds!.volume
      );
      
      const totalScore = calculateTotalScore(
        twitterEngagement,
        marketCapScore,
        rankScore,
        volumeScore
      );
      
      // Get the correct subnet name using our utility function
      const correctName = getCorrectSubnetName(subnet.netuid, subnetNames);
      
      return {
        netuid: subnet.netuid,
        name: correctName,
        mindsharePercentage: 0, // Will be calculated later
        twitterEngagement,
        marketCapScore,
        rankScore,
        volumeScore,
        totalScore,
        lastUpdated: new Date().toISOString()
      };
    });
    
    // Apply minimum baseline scores to prevent complete zeroing out
    mindshareScores = applyMinimumBaseline(mindshareScores);
    
    // Calculate mindshare percentages
    const mindshareWithPercentages = calculateMindsharePercentages(mindshareScores);
    
    // Sort by mindshare percentage (descending)
    const result = mindshareWithPercentages.sort((a, b) => 
      b.mindsharePercentage - a.mindsharePercentage
    );
    
    return result;
  } catch (error) {
    console.error('Error calculating subnet mindshare:', error);
    return [];
  }
};